import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { LANGUAGES, DEFAULT_LANGUAGE } from "@/constants/languages";
import { isSupportedLanguage } from "@/utils/isSupportedLanguage";

function useLanguageSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { locale = DEFAULT_LANGUAGE } = useParams();

  const currentLanguage = i18n.language.split("-")[0];

  const changeLanguage = (language: string) => {
    if (language === currentLanguage) return;

    const queryParams = new URLSearchParams(location.search).toString();

    const currentPath = location.pathname
      .split("/")
      .slice(currentLanguage === DEFAULT_LANGUAGE ? 1 : 2)
      .join("/");

    const newPath =
      language === DEFAULT_LANGUAGE
        ? `/${currentPath}`
        : `/${language}/${currentPath}`;

    const finalPath = queryParams ? `${newPath}?${queryParams}` : newPath;

    navigate(finalPath, { replace: true });
    i18n.changeLanguage(language);
  };

  useEffect(() => {
    if (isSupportedLanguage(locale)) {
      if (
        locale === DEFAULT_LANGUAGE &&
        location.pathname.startsWith(`/${DEFAULT_LANGUAGE}/`)
      ) {
        const queryParams = new URLSearchParams(location.search).toString();

        const newPath = location.pathname.replace(`/${DEFAULT_LANGUAGE}`, "");

        const finalPath = queryParams ? `${newPath}?${queryParams}` : newPath;

        navigate(finalPath || "/", { replace: true });
      } else {
        i18n.changeLanguage(locale);
      }
    } else {
      navigate("/", { replace: true });
    }
  }, [locale, i18n, navigate, location.pathname, location.search]);

  return {
    changeLanguage,
    currentLanguage,
    languages: LANGUAGES,
  };
}

export default useLanguageSwitcher;
